/**
 * LOS CRUCES QUE EL TELÉFONO VIO Y NO PUDO CONTAR TODAVÍA.
 *
 * El rastreo decide solo cuándo se entra y cuándo se sale de una obra —lleva
 * su propio registro y solo habla cuando eso cambia—, pero en obra sin
 * cobertura no tiene a quién decírselo. Antes eso significaba perderlo: el
 * cruce se daba por contado y en la siguiente lectura ya no había borde que
 * cruzar. Una salida que no se reporta es una orden que se queda abierta y
 * unas horas que nadie suma.
 *
 * Aquí se guardan con SU HORA, que es lo único que no se puede reconstruir
 * después. La base la respeta (`p_ocurrido_en`, migración 0213) con sus
 * límites: nada del futuro más allá de cinco minutos, nada de hace más de
 * tres días.
 *
 * Es deliberadamente localStorage y no IndexedDB: son cuatro números por
 * cruce, no hay fotos, y tiene que poder escribirse desde el servicio en
 * segundo plano sin ceremonia. Las fotos y los marcajes a mano sí van a
 * IndexedDB, que es donde pesan.
 */
import { supabase } from '@/lib/supabase/cliente'

const LLAVE = 'mega.posiciones-pendientes'

/**
 * Cuántas se guardan como mucho.
 *
 * Un cruce son decenas de bytes y en una jornada normal hay dos o tres por
 * obra. Doscientos cubre varios días sin señal de sobra; pasado eso se tiran
 * las más viejas, que además la base ya no aceptaría: más de tres días y
 * `fn_hora_del_marcaje` las fecha con su reloj, que es justo lo que se
 * quería evitar.
 */
const TOPE = 200

export type PosicionPendiente = {
  latitud: number
  longitud: number
  exactitud: number | null
  esLatido: boolean
  /** Cuándo la tomó el teléfono. Es el dato que no se puede recuperar luego. */
  ocurridoEn: string
}

function leer(): PosicionPendiente[] {
  try {
    const crudo = localStorage.getItem(LLAVE)
    return crudo ? (JSON.parse(crudo) as PosicionPendiente[]) : []
  } catch {
    return []
  }
}

function escribir(cola: PosicionPendiente[]): void {
  try {
    localStorage.setItem(LLAVE, JSON.stringify(cola.slice(-TOPE)))
  } catch {
    /* almacenamiento lleno: se pierde esta, no la app */
  }
}

/** Guarda un cruce para cuando haya señal. */
export function encolarPosicion(p: PosicionPendiente): void {
  escribir([...leer(), p])
}

export function cuantasEsperan(): number {
  return leer().length
}

/**
 * Sube lo que esperaba, en orden y de una en una.
 *
 * EN ORDEN Y DE UNA EN UNA a propósito: la base lee la última visita de cada
 * orden para decidir qué significa el punto que acaba de llegar. Mandarlas a
 * la vez haría que una salida se juzgara contra una llegada que todavía no
 * entró, y saldrían llegadas sin salida y horas que no son.
 *
 * Si una falla se deja y se corta: la siguiente dependería de ella.
 */
export async function subirPosicionesPendientes(): Promise<number> {
  const cola = leer()
  if (cola.length === 0) return 0

  let subidas = 0

  for (const p of cola) {
    const { error } = await supabase.rpc('fn_reportar_posicion', {
      p_latitud: p.latitud,
      p_longitud: p.longitud,
      p_exactitud_m: p.exactitud ?? 9999,
      p_bateria: null,
      p_origen: 'android',
      p_es_latido: p.esLatido,
      p_ocurrido_en: p.ocurridoEn,
    })

    if (error) break
    subidas += 1
  }

  escribir(cola.slice(subidas))
  return subidas
}
